import React, { useState } from "react";
import {
  TrendingUp,
  Globe,
  Search,
  Plus,
  Clock,
  Users,
  Calendar,
  FileText,
  Link,
  AlertCircle,
  BarChart3,
  Brain,
  Sparkles,
  CheckCircle,
  ArrowRight,
  Loader2,
  ExternalLink,
  Filter,
} from "lucide-react";
import Is1 from "../assets/insight1.png";
import Is2 from "../assets/insight2.png";
import Is3 from "../assets/insight3.png";
import Is4 from "../assets/insight4.png";
import Is5 from "../assets/insight5.png";

const UsageInsightAnalyze = () => {
  const [selectedModel, setSelectedModel] = useState("Elva_BrowserX4");
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const ModelOption = ({
    model,
    agents,
    description,
    icon: Icon,
    color,
    recommended,
  }) => (
    <div
      className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
        selectedModel === model
          ? `bg-${color}-50 border-${color}-500 shadow-lg`
          : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-md"
      }`}
      onClick={() => setSelectedModel(model)}
    >
      {recommended && (
        <div className="absolute -top-3 -right-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs px-3 py-1 rounded-full font-medium">
          추천
        </div>
      )}
      <div className="flex items-start gap-4">
        <div
          className={`w-14 h-14 bg-${color}-100 rounded-xl flex items-center justify-center`}
        >
          <Icon className={`w-7 h-7 text-${color}-600`} />
        </div>
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-gray-800 mb-1">{model}</h4>
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">{agents}명의 AI Agent</span>
          </div>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>
      {selectedModel === model && (
        <div
          className={`absolute bottom-3 right-3 w-6 h-6 bg-${color}-500 rounded-full flex items-center justify-center`}
        >
          <CheckCircle className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  );

  const FeatureCard = ({ icon: Icon, title, description, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
      <div
        className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center mb-4`}
      >
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
      <h3 className="font-semibold text-gray-800 mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-96 h-96 bg-blue-400/20 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-purple-400/20 rounded-full filter blur-3xl animate-pulse delay-700"></div>
        </div>

        <div className="relative container mx-auto px-6">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full text-blue-700 text-sm font-medium">
              <TrendingUp className="w-4 h-4" />
              AI-Powered Web Analysis
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                인사이트 분석 시스템
              </span>
            </h1>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Elva가 직접 웹을 탐색하고 분석하여 실시간 인사이트를 제공합니다.
              학습 데이터의 한계를 넘어 최신 정보를 수집하고 정리해드립니다.
            </p>

            <div className="flex flex-wrap justify-center gap-6 mt-8">
              <FeatureCard
                icon={Globe}
                title="실시간 웹 탐색"
                description="AI가 직접 웹사이트 방문"
                color="blue"
              />
              <FeatureCard
                icon={Brain}
                title="지능형 분석"
                description="수집된 데이터 자동 정리"
                color="purple"
              />
              <FeatureCard
                icon={BarChart3}
                title="인사이트 도출"
                description="체계적인 보고서 생성"
                color="green"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Limitations Notice */}
      <section className="py-8 bg-amber-50 border-y border-amber-200">
        <div className="container mx-auto px-6">
          <div className="flex items-start gap-4 max-w-4xl mx-auto">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">
                서비스 이용 안내
              </h3>
              <ul className="space-y-1 text-gray-700">
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span>분석 시간은 최대 5분까지 소요될 수 있습니다</span>
                </li>
                <li className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-amber-600" />
                  <span>
                    높은 서버 사용량으로 특정 시점에 이용이 제한될 수 있습니다
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Step by Step Guide */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            <h2 className="text-3xl font-bold text-center text-gray-800">
              사용 가이드
            </h2>

            {/* Step 1 */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    작업 목록 생성
                  </h3>
                </div>
              </div>
              <div className="p-8 space-y-6">
                <img
                  src={Is1}
                  alt="작업 목록 생성"
                  className="w-full rounded-lg shadow-md"
                />
                <p className="text-gray-600">
                  + 버튼을 클릭하여 새로운 작업 목록을 생성합니다. 작업 목록은
                  다양한 인사이트 분석 결과를 관리하고 저장하기 위한
                  시스템입니다.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    작업 이름 설정
                  </h3>
                </div>
              </div>
              <div className="p-8 space-y-6">
                <img
                  src={Is2}
                  alt="작업 이름 입력"
                  className="w-full rounded-lg shadow-md"
                />
                <p className="text-gray-600">
                  작업자가 알아볼 수 있는 작업 이름을 입력해주세요.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-green-600 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    분석창 생성 완료
                  </h3>
                </div>
              </div>
              <div className="p-8 space-y-6">
                <img
                  src={Is3}
                  alt="분석창 생성"
                  className="w-full rounded-lg shadow-md"
                />
                <p className="text-gray-600">
                  새로운 인사이트 분석창이 열린 것을 확인할 수 있습니다.
                </p>
              </div>
            </div>

            {/* Model Selection */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
                AI Agent 모델 선택
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <ModelOption
                  model="Elva_BrowserX4"
                  agents={4}
                  description="표준적인 분석 속도와 품질을 제공합니다. 일반적인 인사이트 분석에 적합합니다."
                  icon={Search}
                  color="blue"
                  recommended={true}
                />
                <ModelOption
                  model="Elva_BrowserX6"
                  agents={6}
                  description="더 많은 정보원에서 데이터를 수집하여 심층적인 분석을 제공합니다."
                  icon={Globe}
                  color="purple"
                />
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    분석 조건 설정
                  </h3>
                </div>
              </div>
              <div className="p-8 space-y-6">
                <img
                  src={Is4}
                  alt="분석 조건 설정"
                  className="w-full rounded-lg shadow-md"
                />
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <Search className="w-4 h-4 text-indigo-600" />
                      인사이트 내용
                    </h4>
                    <p className="text-sm text-gray-600">
                      찾고자 하는 주제나 키워드 입력
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-purple-600" />
                      수집 기간
                    </h4>
                    <p className="text-sm text-gray-600">
                      데이터 수집 기간 설정
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <Filter className="w-4 h-4 text-blue-600" />
                      사이트 유형
                    </h4>
                    <p className="text-sm text-gray-600">
                      주로 수집할 사이트 형태 선택
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-green-600" />
                      출력 형식
                    </h4>
                    <p className="text-sm text-gray-600">
                      보고서 출력 형식 지정
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 - Analysis Result */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">5</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    분석 결과
                  </h3>
                </div>
              </div>
              <div className="p-8 space-y-6">
                <img
                  src={Is5}
                  alt="분석 결과"
                  className="w-full rounded-lg shadow-md"
                />
                <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-500" />
                    분석 완료 후 제공되는 정보
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-800">
                          인사이트 보고서
                        </p>
                        <p className="text-sm text-gray-600">
                          수집된 데이터를 기반으로 작성된 체계적인 분석 보고서
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Link className="w-5 h-5 text-blue-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-800">
                          참조 사이트 목록
                        </p>
                        <p className="text-sm text-gray-600">
                          보고서 작성에 사용된 모든 웹사이트 링크
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Analysis Progress Animation */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-semibold mb-6 text-center">
                분석 진행 과정
              </h3>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-3">
                    <Search className="w-8 h-8" />
                  </div>
                  <p className="text-sm">키워드 검색</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-3">
                    <Globe className="w-8 h-8" />
                  </div>
                  <p className="text-sm">웹사이트 방문</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-3">
                    <Brain className="w-8 h-8" />
                  </div>
                  <p className="text-sm">정보 분석</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-8 h-8" />
                  </div>
                  <p className="text-sm">보고서 생성</p>
                </div>
              </div>
              {isAnalyzing && (
                <div className="mt-6 flex items-center justify-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>분석 진행 중... (최대 5분 소요)</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-16 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            실시간 웹 인사이트를 경험하세요
          </h2>
          <p className="text-indigo-100 mb-8 max-w-2xl mx-auto">
            Elva Browser가 직접 웹을 탐색하고 분석하여 최신 정보 기반의
            인사이트를 제공합니다
          </p>
          <button
            className="bg-white text-indigo-600 px-8 py-3 rounded-full font-semibold hover:bg-indigo-50 transition-colors shadow-lg inline-flex items-center gap-2"
            onClick={() => setIsAnalyzing(true)}
          >
            <TrendingUp className="w-5 h-5" />
            인사이트 분석 시작하기
          </button>
        </div>
      </section> */}
    </div>
  );
};

export default UsageInsightAnalyze;
